import React, { createContext, useReducer } from "react";
import {
  attempCourseCreation,
  attempFlippedLectureCreation,
  attempLiveLectureCreation,
  retrieveCourseInfo,
  retrieveLectureInfo,
  retrieveTaughtCourses,
  attempCourseDeletion,
  attempLectureDeletion,
  attempUpdateLecture,
  attempUploadSlides,
  attempDeleteSlides,
} from "../api/teacher";
import TeacherReducer, {
  ADD_COURSE,
  ADD_LECTURE,
  SELECT_LECTURE,
  UPDATE_COURSE,
  UPDATE_COURSES,
  DELETE_COURSE,
  DELETE_LECTURE,
  UPDATE_LECTURE,
  TEACHER_FETCH_START,
  TEACHER_FETCH_SUCCESS,
  TEACHER_FETCH_FAILURE,
  RESET_ERROR,
} from "./TeacherReducer";

/**
 * TODO: state could be refactored with courses and lectures being objects identified by uuid
 * -> faster and easier access to update the state
 *
 * courses: {
 *  course_uuid1: { ..., lectures: { lecture_uuid1: { ... }, lecture_uuid2: { ... } } },
 *  course_uuid2: { ... },
 * }
 *
 */

//Initial state
const initialState = {
  courses: null, //The list of taught courses by the current user
  currentLecture: null, //The current lecture opened by the user
  loading: false,
  error: null,
};

//Create context
export const TeacherContext = createContext(initialState);

//Provider component
export const TeacherProvider = ({ children }) => {
  const [state, dispatch] = useReducer(TeacherReducer, initialState);

  /**
   * Retrieves the list of taught courses by the current user and updates the state
   */
  const getCourses = async () => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const courses = await retrieveTaughtCourses();
      dispatch({ type: UPDATE_COURSES, payload: courses });
      return courses;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  /**
   * Obtains the list of lectures of a given course and updates the state
   *
   * @param {string} uuid - the uuid of the course
   */
  const getCourseInfo = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const info = await retrieveCourseInfo(uuid);
      dispatch({ type: UPDATE_COURSE, payload: info });
      return info;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  /**
   * Retrieves lecture info and updates the state
   *
   * @param {string} uuid - the lecture uuid
   */
  const selectLecture = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const lecture = await retrieveLectureInfo(uuid);
      dispatch({
        type: SELECT_LECTURE,
        payload: lecture,
      });
      return lecture;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  const getLectureInfo = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const info = await retrieveLectureInfo(uuid);
      dispatch({ type: TEACHER_FETCH_SUCCESS });
      return info;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  /**
   * Creates a new course
   *
   * @param {string} name - the name of the course
   */
  const createCourse = async (name) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const course = await attempCourseCreation(name);
      dispatch({ type: ADD_COURSE, payload: course });
      return course;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  const resetError = () => {
    dispatch({ type: RESET_ERROR });
  };

  /**
   * Creates a new flipped lecture
   *
   * @param {string} courseUUID - the UUID of the course to which the flipped lecture belongs
   * @param {Object} data - the data of the flipped lecture
   * @param {string} data.name - the title of the flipped lecture
   * @param {string} data.videoId - the ID of the youtube video of the flipped lecture
   * @param {bool} data.published - whether the flipped lecture is public or not
   * @param {file} slides - slides of the lecture
   */
  const createFlippedLecture = async (courseUUID, { name, videoId, published, slides }) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const lecture = await attempFlippedLectureCreation(courseUUID, { name, videoId, published });
      if (slides) await attempUploadSlides({ slides, lectureUUID: lecture.uuid });
      dispatch({
        type: ADD_LECTURE,
        payload: {
          courseUUID,
          lecture,
        },
      });
      return lecture;
    } catch (err) {
      console.log(err);
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  /**
   * Creates a new live lecture
   *
   * @param {string} courseUUID - the UUID of the course to which the live lecture belongs
   * @param {Object} data - the data of the live lecture
   * @param {string} data.name - the title of the live lecture
   * @param {string} data.startTime - the start time of the live lecture in ISO format
   * @param {string} data.endTime - the end time of the live lecture in ISO format
   * @param {string} data.videoId - the ID of the youtube video of the live lecture
   * @param {bool} data.published - whether the live lecture is public or not
   * @param {file} slides - slides of the lecture
   */
  const createLiveLecture = async (courseUUID, { name, videoId, startTime, endTime, published, slides }) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const lecture = await attempLiveLectureCreation(courseUUID, { name, startTime, endTime, videoId, published });
      if (slides) await attempUploadSlides({ slides, lectureUUID: lecture.uuid });
      dispatch({
        type: ADD_LECTURE,
        payload: {
          courseUUID,
          lecture,
        },
      });
      return lecture;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
      return null;
    }
  };

  /**
   * Updates a live lecture information
   *
   * @param {string} courseUUID - the UUID of the course of the lecture to be updated
   * @param {Object} data - the data of the live lecture
   * @param {string} data.lectureUUID - the uuid of the lecture to be updated
   * @param {string} data.name - the title of the live lecture
   * @param {string} data.startTime - the start time of the live lecture in ISO format
   * @param {string} data.endTime - the end time of the live lecture in ISO format
   * @param {string} data.videoId - the ID of the youtube video of the live lecture
   * @param {bool} data.published - whether the live lecture is public or not
   * @param {file} slides - slides of the lecture
   */
  const updateLiveLecture = async (
    courseUUID,
    { lectureUUID, name, videoId, startTime, endTime, published, slides }
  ) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const lecture = await attempUpdateLecture({ lectureUUID, name, published, startTime, endTime, videoId });
      if (slides) await attempUploadSlides({ slides, lectureUUID: lecture.uuid });

      dispatch({
        type: UPDATE_LECTURE,
        payload: {
          courseUUID,
          lecture,
        },
      });
      return lecture;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Updates a flipped lecture information
   *
   * @param {string} courseUUID - the UUID of the course of the lecture to be updated
   * @param {Object} data - the data of the flipped lecture
   * @param {string} data.lectureUUID - the uuid of the lecture to be updated
   * @param {string} data.name - the title of the flipped lecture
   * @param {string} data.videoId - the ID of the youtube video of the flipped lecture
   * @param {bool} data.published - whether the flipped lecture is public or not
   */
  const updateFlippedLecture = async (courseUUID, { lectureUUID, name, videoId, published }) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      const lecture = await attempUpdateLecture({ lectureUUID, name, published, videoId });
      dispatch({
        type: UPDATE_LECTURE,
        payload: {
          courseUUID,
          lecture,
        },
      });

      return lecture;
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Deletes a course
   *
   * @param {string} uuid - the UUID of the lecture to be deleted
   */
  const deleteCourse = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      await attempCourseDeletion(uuid);
      dispatch({ type: DELETE_COURSE, payload: uuid });
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Deletes a lecture
   *
   * @param {string} uuid - the UUID of the lecture to be deleted
   */
  const deleteLecture = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      await attempLectureDeletion(uuid);
      dispatch({ type: DELETE_LECTURE, payload: uuid });
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Upload slides
   *
   * @param {file} slides - slides of the lecture
   * @param {string} uuid - the UUID of the lecture
   */
  const uploadSlides = async ({ slides, lectureUUID }) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      await attempUploadSlides({ slides, lectureUUID });
      dispatch({ type: TEACHER_FETCH_SUCCESS });
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  /**
   * Delete slides
   *
   * @param {string} uuid - the UUID of the lecture
   */
  const deleteSlides = async (uuid) => {
    dispatch({ type: TEACHER_FETCH_START });
    try {
      await attempDeleteSlides(uuid);
      dispatch({ type: TEACHER_FETCH_SUCCESS });
    } catch (err) {
      dispatch({ type: TEACHER_FETCH_FAILURE, payload: err });
    }
  };

  const getSlidesURL = (lectureUUID) => {
    const slidesURL =
      process.env.NODE_ENV === "production"
        ? `${process.env.REACT_APP_SLIDES_PROD_URL}${lectureUUID}/slides`
        : `${process.env.REACT_APP_SLIDES_DEV_URL}${lectureUUID}/slides`;
    return slidesURL;
  };

  return (
    <TeacherContext.Provider
      value={{
        courses: state.courses,
        currentLecture: state.currentLecture,
        loading: state.loading,
        error: state.error,
        getCourses,
        getCourseInfo,
        selectLecture,
        createCourse,
        createFlippedLecture,
        createLiveLecture,
        updateLiveLecture,
        updateFlippedLecture,
        deleteCourse,
        deleteLecture,
        getLectureInfo,
        uploadSlides,
        deleteSlides,
        getSlidesURL,
        resetError,
      }}
    >
      {children}
    </TeacherContext.Provider>
  );
};
